import {
  ChartConfig,
  ChartCurrencies,
  ChartSeries,
  ChartTimespan,
  MarketHistorySeries,
  ChartVisibleRateTypes,
} from 'models/chart'
import currencyChartHelper from './currency-chart-helper'
import { getTranslationsMap } from 'i18n/i18n'
import marketService from 'services/market-service'
import { MarketBrief, MarketHistory } from 'models/market'
import { t9n } from 'src/assets/scripts/t9n/t9n'
import { getRatePrecision } from 'src/assets/scripts/utils/format-number'

declare const USER_BASE_URL_WT: string
declare const window: any

const initialChartConfig: ChartConfig = {
  timeRange: '1-month',
  showForex: true,
  showAsk: true,
  showBid: true,
  sampleSpan: '1h',
}

const chartCurrencies: ChartCurrencies = {
  baseCurrency: null,
  counterCurrency: null,
}

const currencyChart = {
  chart: null,
  marketBrief: null,
  pair: null,
  currencies: chartCurrencies,
  config: initialChartConfig,
  chartPeriodButtons: null,

  init: (currencies: ChartCurrencies): void => {
    currencyChart.pair = `${currencies.baseCurrency}_${currencies.counterCurrency}`
    currencyChart.currencies = currencies
    currencyChart.config = {
      ...initialChartConfig,
      ...currencyChartHelper.getLocalStorageConfig(),
    }

    currencyChart.chartPeriodButtons = document.querySelectorAll('[data-chart-period]')

    currencyChart.recreateGUIState()

    currencyChart.initChart().then(() => {
      currencyChart.initListeners()
    })
  },

  createScriptTag: (chartOptions): void => {
    const chart = document.querySelector('#currency-chart')

    try {
      localStorage.setItem('chartLangData', JSON.stringify(getTranslationsMap().highcharts))
      localStorage.setItem('chartOptions', JSON.stringify(chartOptions))
    } catch {
      window.chartLangData = JSON.stringify(getTranslationsMap().highcharts)
      window.chartOptions = JSON.stringify(chartOptions)
    }

    const script = document.createElement('script')
    script.setAttribute('type', 'module')
    script.setAttribute('async', '')
    script.innerHTML = `
      import 'https://code.highcharts.com/stock/5.0.14/highstock.js'

      let chartLangData = ''
      let chartOptions = ''
      
      try {
        chartLangData = localStorage.getItem('chartLangData')
        chartOptions = localStorage.getItem('chartOptions')
      } catch {
        chartLangData = window.chartLangData
        chartOptions = window.chartOptions
      }
      
      Highcharts.setOptions({
        lang: JSON.parse(chartLangData),
        global: {
          useUTC: false,
        },
      })
      window.chart = Highcharts.stockChart('currency-chart', JSON.parse(chartOptions))
    `
    const drawChartOnScroll = () => {
      if (chart.getBoundingClientRect().y < window.innerHeight) {
        document.head.append(script)
        window.removeEventListener('scroll', drawChartOnScroll)
      }
    }

    if (chart.getBoundingClientRect().y < window.innerHeight) {
      document.head.append(script)
    } else {
      window.addEventListener('scroll', drawChartOnScroll)
    }
  },

  fetchCurrentChartData: (): Promise<MarketHistory | null> => {
    const marketHistory = marketService
      .fetchMarketHistory(
        USER_BASE_URL_WT,
        currencyChart.currencies.baseCurrency,
        currencyChart.currencies.counterCurrency,
        currencyChart.config.timeRange,
      )
      .then((response) => {
        currencyChart.config.sampleSpan = response.sample_span
        return response
      })
      .catch(() => {
        const $errorSpinner = document.querySelector<HTMLDivElement>('[data-spinner]')
        if ($errorSpinner) {
          $errorSpinner.querySelector('[data-spinner-text]').textContent = t9n('formValidation.serverUnknownError')
          $errorSpinner.hidden = false
        }
        return null
      })

    return marketHistory
  },

  initChart: (): Promise<void> => {
    return currencyChart.fetchCurrentChartData().then((data) => {
      if (data) {
        const marketSeries = currencyChart.prepareSeries(data)

        const visibleRateTypes: ChartVisibleRateTypes = {
          bid: currencyChart.config.showBid,
          ask: currencyChart.config.showAsk,
          forex: currencyChart.config.showForex,
        }

        const series = currencyChartHelper.prepareSeriesForChart(marketSeries, visibleRateTypes)
        const chartOptions = currencyChart.getChartOptions(series)

        currencyChart.createScriptTag(chartOptions)
      }
    })
  },

  prepareSeries: (marketHistory: MarketHistory): MarketHistorySeries => {
    const currentMarketBrief = currencyChartHelper.getCurrentMarketBrief(currencyChart.marketBrief)
    return currencyChartHelper.updateSeriesWithMarketBrief(
      marketHistory.fx_series,
      marketHistory.wt_series,
      currentMarketBrief,
    )
  },

  getChartOptions: (series: ChartSeries) => {
    const ratePrecision = getRatePrecision(currencyChart.pair)
    const defaultChartOptions = currencyChartHelper.getDefaultChartOptions(
      currencyChart.config.sampleSpan,
      ratePrecision,
    )
    return {
      ...defaultChartOptions,
      series: [series.bid, series.ask, series.forex],
    }
  },

  updateMarketBrief: (marketBrief: MarketBrief, noUpdate?: boolean): void => {
    if (!noUpdate) {
      currencyChart.marketBrief = marketBrief
    }

    const finishedAnimating = window.chart?.series && window.chart.series.some((s) => s.finishedAnimating)

    // First loading, add marketBrief points after animation
    if (!finishedAnimating) {
      setTimeout(() => {
        currencyChart.updateMarketBrief(marketBrief, true)
      }, 100)
    } else {
      currencyChart.updateChart()
    }
  },

  updateChart: (): void => {
    currencyChart.fetchCurrentChartData().then(currencyChart.updateChartPoints).catch()
  },

  updateChartPoints: (marketHistory: MarketHistory): void => {
    const [bid, ask, forex] = window.chart.series

    const series = currencyChart.prepareSeries(marketHistory)

    currencyChart.addPointsToChart(forex, series.fxSeries, ([ts, fx]) => [ts * 1000, fx])
    currencyChart.addPointsToChart(bid, series.wtBidSeries, ([ts, bidClose]) => [ts * 1000, bidClose])
    currencyChart.addPointsToChart(ask, series.wtAskSeries, ([ts, askClose]) => [ts * 1000, askClose])

    window.chart.redraw(/* animation */ true)
  },

  addPointsToChart: (chartSeries, newDataArray: [number, number][], xyMapper): void => {
    if (!chartSeries.data) {
      return
    }
    const pointsMap = chartSeries.data.reduce((map, p) => map.set(p.x, p), new Map())
    newDataArray.forEach((newData) => {
      const [x, y] = xyMapper(newData)
      const point = pointsMap.get(x)
      if (point && point.y !== y) {
        point.update(y, /* redraw */ false)
      } else if (!point) {
        chartSeries.addPoint([x, y], /* redraw */ false, /* shift */ true)
      }
    })
  },

  toggleSeriesVisibility: (seriesIndex: string, seriesState: boolean): void =>
    seriesState ? window.chart.series[seriesIndex].show() : window.chart.series[seriesIndex].hide(),

  initListeners: (): void => {
    ;['data-showForex-visibility', 'data-showAsk-visibility', 'data-showBid-visibility'].map((elIndex) => {
      document.querySelector(`[${elIndex}]`).addEventListener('change', (e) => {
        if (e.currentTarget instanceof Element) {
          const currentState = e.currentTarget.getAttribute(elIndex) === 'true'
          currencyChart.setSwitcherState(elIndex, !currentState)
          currencyChart.toggleSeriesVisibility(e.currentTarget.getAttribute('data-series'), !currentState)

          const inputName = elIndex.replace('data-', '').replace('-visibility', '')

          currencyChart.config[inputName] = !currentState

          currencyChartHelper.storeLocalStorageConfig(currencyChart.config)
        }
      })
    })

    currencyChart.chartPeriodButtons.forEach((item) => {
      item.addEventListener('click', (e) => {
        currencyChart.chartPeriodButtons.forEach((item) => {
          item.removeAttribute('data-chart-selected-period')
          item.classList.remove('active')
        })

        if (e.currentTarget instanceof Element) {
          e.currentTarget.classList.add('active')
          e.currentTarget.setAttribute('data-chart-selected-period', '')

          currencyChart.config.timeRange = e.currentTarget.getAttribute('data-chart-period') as ChartTimespan
          currencyChartHelper.storeLocalStorageConfig(currencyChart.config)

          currencyChart.initChart()
        }
      })
    })
  },

  setSwitcherState: (dataAttribute: string, dataState: boolean) => {
    document.querySelector(`[${dataAttribute}]`).setAttribute(dataAttribute, dataState.toString())

    const inputName = dataAttribute.replace('data-', '').replace('-visibility', '')
    if (dataState) {
      document.querySelector(`input[name='${inputName}']`).setAttribute('checked', 'true')
    } else {
      document.querySelector(`input[name='${inputName}']`).removeAttribute('checked')
    }
  },

  setPeriodState: (ident: string): void => {
    currencyChart.chartPeriodButtons.forEach((item) => {
      item.classList.remove('active')
    })
    document.querySelector(ident).classList.add('active')
  },

  recreateGUIState: (): void => {
    document
      .querySelector('[data-showForex-visibility]')
      .setAttribute('data-showForex-visibility', currencyChart.config.showForex.toString())
    currencyChart.setSwitcherState('data-showForex-visibility', currencyChart.config.showForex)

    document
      .querySelector('[data-showAsk-visibility]')
      .setAttribute('data-showAsk-visibility', currencyChart.config.showAsk.toString())
    currencyChart.setSwitcherState('data-showAsk-visibility', currencyChart.config.showAsk)

    document
      .querySelector('[data-showBid-visibility]')
      .setAttribute('data-showBid-visibility', currencyChart.config.showBid.toString())
    currencyChart.setSwitcherState('data-showBid-visibility', currencyChart.config.showBid)

    currencyChart.setPeriodState(`[data-chart-period='${currencyChart.config.timeRange}']`)
  },
}

export default currencyChart
